import React, { memo, useMemo, useState } from 'react';
import { View, Text, StyleSheet, useWindowDimensions } from 'react-native';
import { resolveWebLayoutWidth } from '../../../shared/platform/webLayout';
import { getLocale } from '../../../shared/i18n';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Animated, {
  useAnimatedStyle,
  useDerivedValue,
  useAnimatedReaction,
  runOnJS,
  interpolate,
  interpolateColor,
  Extrapolation,
} from 'react-native-reanimated';
import moment from 'moment';
import { MenuFlowDateSliderSet2 } from './MenuFlowDateSliderSet2';
import { MenuFlowCountersRowSet2 } from '../../../foodMenu/variants/set2/MenuFlowCountersRowSet2';
import { SECTION_HEADER_HEIGHT } from '../../components/MenuFlowSectionHeader';
import { MenuFlowSectionHandoffSet2 } from './MenuFlowSectionHandoffSet2';
import {
  MENU_SET2_SECTION_HEADER_BOTTOM,
  MENU_SET2_SECTION_HEADER_BOTTOM_COLLAPSED,
} from '../../../foodMenu/components/menuFlowHeaderLayout';
import { AnimatedSection } from '../../../components/common/AnimatedSection';
import { appTheme, useThemeColors, useThemeStore, type ThemeColors } from '../../../shared/theme';
import type { MenuFlowHeaderProps } from '../../components/MenuFlowHeader';

const DATE_SLIDER_HEIGHT = 60;
const MONTH_ROW_HEIGHT = 20;
const SECTION_HEADER_GAP = 8;
const SCALE_COLLAPSED = 0.96;
const DATE_SLIDER_MARGIN_BOTTOM = 8;

function createMenuFlowHeaderStyles(c: ThemeColors) {
  return StyleSheet.create({
    headerWrap: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 5000000,
      overflow: 'visible',
      flexDirection: 'column',
    },
    headerContentAnim: {
      overflow: 'hidden',
      flexDirection: 'column',
    },
    monthNameWrap: {
      overflow: 'hidden',
      height: MONTH_ROW_HEIGHT,
      justifyContent: 'center',
    },
    dateSliderWrap: { overflow: 'hidden' },
    countersDoubleBoxWrap: {
      flexDirection: 'column',
      height: 40,
      justifyContent: 'center',
    },
    foodTypeSectionHeaderWrap: {
      overflow: 'hidden',
      height: SECTION_HEADER_HEIGHT,
      justifyContent: 'center',
      alignSelf: 'center',
      marginTop: SECTION_HEADER_GAP,
    },
    month: {
      fontSize: 18,
      fontWeight: '600',
      lineHeight: MONTH_ROW_HEIGHT,
      letterSpacing: 0.3,
      color: c.textInverse,
      textAlign: 'center',
      width: '100%',
    },
  });
}

const MenuFlowHeaderSet2Component = ({
  topOffset = 0,
  scrollY,
  scrollRange,
  fullHeaderHeight,
  collapsedHeaderHeight,
  dateSliderDates,
  selectableDates,
  selectedDate,
  onDateSelect,
  dayConfigMap,
  mealsCount,
  snacksCount,
  selectedMealsCount,
  selectedSnacksCount,
  lastDateSliderCollapsedSV,
  reduceMotion: _reduceMotion = false,
  sections = [],
  currentSectionIndexSV,
  isDoubleBoxForDate = false,
  activeBox = 'box1',
  onBoxSelect,
  forcedDisabledDates = [],
  onCancelBox,
  onRequestDoubleBox,
}: MenuFlowHeaderProps) => {
  const insets = useSafeAreaInsets();
  const c = useThemeColors() ?? useThemeStore.getState().colors;
  const header_styles = useMemo(() => createMenuFlowHeaderStyles(c), [c]);
  const [localSectionIndex, setLocalSectionIndex] = useState(0);
  const [isDateSliderCollapsedLocal, setIsDateSliderCollapsedLocal] = useState(false);

  const month_label = useMemo(() => {
    if (!selectedDate) {
      return '';
    }
    const loc = getLocale() === 'ar' ? 'ar' : 'en';
    try {
      moment.locale(loc);
    } catch {
      moment.locale('en');
    }
    return moment(selectedDate).format('MMMM');
  }, [selectedDate]);

  useAnimatedReaction(
    () => currentSectionIndexSV.value,
    idx => {
      runOnJS(setLocalSectionIndex)(Math.max(0, Math.floor(idx)));
    },
    [currentSectionIndexSV],
  );

  useAnimatedReaction(
    () => lastDateSliderCollapsedSV.value,
    collapsed => {
      runOnJS(setIsDateSliderCollapsedLocal)(collapsed);
    },
    [lastDateSliderCollapsedSV],
  );

  const collapsed_height = useMemo(() => collapsedHeaderHeight, [collapsedHeaderHeight]);
  const current_section = useMemo(() => sections[localSectionIndex] ?? sections[0] ?? null, [sections, localSectionIndex]);
  const { width: window_width } = useWindowDimensions();
  const width = resolveWebLayoutWidth(window_width);

  const full_height_num = fullHeaderHeight;
  const collapsed_height_num = collapsed_height;
  const scroll_range_num = scrollRange;
  const safe_top = insets.top + topOffset;
  const color_surface = c.surface;
  const color_border_light = c.borderLight;

  const progress = useDerivedValue(() => {
    'worklet';
    return interpolate(scrollY.value, [0, scroll_range_num], [0, 1], Extrapolation.CLAMP);
  });

  const headerAnimated = useAnimatedStyle(() => {
    'worklet';
    return {
      height: interpolate(progress.value, [0, 1], [full_height_num, collapsed_height_num], Extrapolation.CLAMP),
    };
  });

  const monthNameAnimated = useAnimatedStyle(() => {
    'worklet';
    return {
      height: interpolate(progress.value, [0, 1], [MONTH_ROW_HEIGHT, 0], Extrapolation.CLAMP),
      opacity: interpolate(progress.value, [0, 1], [1, 0], Extrapolation.CLAMP),
    };
  });

  const dateSliderAnimated = useAnimatedStyle(() => {
    'worklet';
    return {
      height: interpolate(progress.value, [0, 1], [DATE_SLIDER_HEIGHT, 0], Extrapolation.CLAMP),
      opacity: interpolate(progress.value, [0, 0.75, 1], [1, 0, 0], Extrapolation.CLAMP),
      marginBottom: interpolate(progress.value, [0, 1], [DATE_SLIDER_MARGIN_BOTTOM, 0], Extrapolation.CLAMP),
      overflow: 'hidden' as const,
    };
  });

  const countersDoubleBoxAnimated = useAnimatedStyle(() => {
    'worklet';
    return { transform: [{ scale: interpolate(progress.value, [0, 1], [1, SCALE_COLLAPSED], Extrapolation.CLAMP) }] };
  });

  const foodTypeSectionHeaderAnimated = useAnimatedStyle(() => {
    'worklet';
    return {
      backgroundColor: color_surface,
      borderRadius: SECTION_HEADER_HEIGHT / 2,
      borderWidth: interpolate(progress.value, [0, 0.85, 1], [0, 0.5, 1], Extrapolation.CLAMP),
      borderColor: interpolateColor(progress.value, [0, 1], [color_surface, color_border_light], 'RGB'),
      opacity: 1,
      marginBottom: interpolate(
        progress.value,
        [0, 1],
        [MENU_SET2_SECTION_HEADER_BOTTOM, MENU_SET2_SECTION_HEADER_BOTTOM_COLLAPSED],
        Extrapolation.CLAMP,
      ),
      transform: [{ scale: interpolate(progress.value, [0, 1], [1, SCALE_COLLAPSED], Extrapolation.CLAMP) }],
    };
  }, [color_surface, color_border_light]);

  const dateSliderPointerEvents = isDateSliderCollapsedLocal ? 'none' : 'auto';

  return (
    <View style={[header_styles.headerWrap, { paddingTop: safe_top }]}>
      <AnimatedSection variant="none" style={[header_styles.headerContentAnim, headerAnimated]}>
      <AnimatedSection variant="none" style={[header_styles.monthNameWrap, monthNameAnimated]}>
        <Text style={header_styles.month} numberOfLines={1}>
          {month_label}
        </Text>
      </AnimatedSection>
      <AnimatedSection variant="none" style={[header_styles.dateSliderWrap, dateSliderAnimated]} pointerEvents={dateSliderPointerEvents}>
        <MenuFlowDateSliderSet2
          availableDates={dateSliderDates}
          selectableDates={selectableDates}
          selectedDate={selectedDate}
          onDateSelect={onDateSelect}
          dayConfigMap={dayConfigMap}
          forcedDisabledDates={forcedDisabledDates}
        />
      </AnimatedSection>
      <AnimatedSection variant="none" style={[header_styles.countersDoubleBoxWrap, countersDoubleBoxAnimated, { pointerEvents: 'auto' }]}>
        <MenuFlowCountersRowSet2
          mealsCount={mealsCount}
          snacksCount={snacksCount}
          selectedMealsCount={selectedMealsCount}
          selectedSnacksCount={selectedSnacksCount}
          isDoubleBoxForDate={isDoubleBoxForDate}
          activeBox={activeBox}
          onBoxSelect={onBoxSelect}
          onCancelBox={onCancelBox}
          onRequestDoubleBox={onRequestDoubleBox}
        />
      </AnimatedSection>
      <AnimatedSection variant="none" style={[header_styles.foodTypeSectionHeaderWrap, foodTypeSectionHeaderAnimated, { width: width - appTheme.spacing.lg * 2 }]}>
        <MenuFlowSectionHandoffSet2
          currentSection={current_section}
          sectionKey={current_section?.foodTypeId ?? localSectionIndex}
          header_collapsed={isDateSliderCollapsedLocal}
        />
      </AnimatedSection>
      </AnimatedSection>
    </View>
  );
};

function menuFlowHeaderSet2PropsAreEqual(prev: MenuFlowHeaderProps, next: MenuFlowHeaderProps): boolean {
  if (
    prev.scrollY !== next.scrollY ||
    prev.scrollRange !== next.scrollRange ||
    prev.fullHeaderHeight !== next.fullHeaderHeight ||
    prev.collapsedHeaderHeight !== next.collapsedHeaderHeight ||
    prev.topOffset !== next.topOffset ||
    prev.selectedDate !== next.selectedDate ||
    prev.mealsCount !== next.mealsCount ||
    prev.snacksCount !== next.snacksCount ||
    prev.selectedMealsCount !== next.selectedMealsCount ||
    prev.selectedSnacksCount !== next.selectedSnacksCount ||
    prev.isSyncing !== next.isSyncing ||
    prev.lastDateSliderCollapsedSV !== next.lastDateSliderCollapsedSV ||
    prev.currentSectionIndexSV !== next.currentSectionIndexSV
  ) {
    return false;
  }
  if (
    prev.dateSliderDates !== next.dateSliderDates ||
    prev.selectableDates !== next.selectableDates ||
    prev.dayConfigMap !== next.dayConfigMap
  ) {
    return false;
  }
  if (
    prev.isDoubleBoxForDate !== next.isDoubleBoxForDate ||
    prev.activeBox !== next.activeBox ||
    prev.onCancelBox !== next.onCancelBox ||
    prev.onRequestDoubleBox !== next.onRequestDoubleBox
  ) {
    return false;
  }
  if (
    prev.forcedDisabledDates?.length !== next.forcedDisabledDates?.length ||
    (prev.forcedDisabledDates?.length && prev.forcedDisabledDates?.[0] !== next.forcedDisabledDates?.[0])
  ) {
    return false;
  }
  return prev.sections === next.sections;
}

export const MenuFlowHeaderSet2 = memo(MenuFlowHeaderSet2Component, menuFlowHeaderSet2PropsAreEqual);
